/**
 * ENG-9567 — production wiring for the restore core: real S3 reads with the
 * control-plane-minted, prefix-scoped credential, and real filesystem writes.
 *
 * Kept apart from `restore-transcripts.ts` so the decision logic stays pure and
 * unit-testable (the drain half splits the same way).
 *
 * THE CREDENTIAL NEVER TOUCHES DISK OR A LOG. It arrives from
 * `POST /agents/:agent/restore-credentials`, lives in this process, and is
 * handed to each `aws` subprocess through its environment. Every failure detail
 * that leaves this module is the subprocess's stderr, truncated — which is why
 * the callers below never interpolate the credential into a message.
 */
import { spawn } from 'node:child_process';
import { existsSync, mkdirSync, readFileSync, renameSync, statSync, unlinkSync } from 'node:fs';
import { basename, dirname } from 'node:path';

import {
  DRAIN_MANIFEST_FILENAME,
  type DrainManifest,
} from './drain-transcript-flush.js';
import {
  markDailySessionSpawn,
  sessionFileExists,
  sessionTranscriptDir,
} from './daily-session.js';
import { getProjectDir, isSessionResumeDisabled } from './persistent-session.js';
import { isOfflineStorageClass, type RestoreDeps } from './restore-transcripts.js';

/** Per-object timeout. An operator command may still not hang forever. */
const AWS_TIMEOUT_MS = 120_000;
/** Listing + manifest fetch are small; a shorter bound keeps a stall obvious. */
const AWS_META_TIMEOUT_MS = 30_000;

export interface RestoreCredential {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
}

export interface RestoreSource {
  bucket: string;
  keyPrefix: string;
  region: string | null;
  credentials: RestoreCredential;
}

/** One object as the listing reports it. */
export interface ListedObject {
  key: string;
  storageClass: string | null;
}

/**
 * Environment for every `aws` subprocess: the minted credential, and no
 * inherited profile.
 *
 * The profile vars are DELETED, not blanked. `AWS_PROFILE=''` is not "no
 * profile" to the CLI — it is a profile whose name is the empty string, and it
 * fails every invocation with `The config profile () could not be found`.
 * Setting it unconditionally (which is what `{...process.env, AWS_PROFILE: ''}`
 * does) therefore breaks the command on EVERY host, including the ones that had
 * no profile set to begin with. Verified against aws-cli 2.35.15.
 *
 * They have to go, though: the minted credential is the whole authorisation, and
 * an inherited profile takes precedence over the keys below — so the read would
 * happen with the WRONG identity, succeeding or failing for reasons unrelated to
 * the scope the control plane granted.
 */
export function awsEnv(src: RestoreSource): NodeJS.ProcessEnv {
  const env: NodeJS.ProcessEnv = { ...process.env };
  delete env['AWS_PROFILE'];
  delete env['AWS_DEFAULT_PROFILE'];
  env['AWS_ACCESS_KEY_ID'] = src.credentials.accessKeyId;
  env['AWS_SECRET_ACCESS_KEY'] = src.credentials.secretAccessKey;
  env['AWS_SESSION_TOKEN'] = src.credentials.sessionToken;
  return env;
}

/**
 * Run one `aws` invocation. Resolves on every failure path (never rejects) and
 * is time-bounded, so a stalled read cannot wedge the command.
 */
/**
 * Accumulate a stream as UTF-8 text.
 *
 * The `setEncoding` is the whole point. Decoding each Buffer chunk on its own
 * (`String(buf)`) mangles any multi-byte UTF-8 sequence that straddles a chunk
 * boundary into replacement characters — and a busy host prefix's listing JSON
 * is comfortably more than one chunk, while an encoded directory segment can
 * carry non-ASCII. The corruption then surfaces far from its cause: as
 * `unparseable listing`, or as a manifest silently skipped and the whole run
 * reporting `absent`. `setEncoding` makes the stream hold back partial
 * sequences until they are complete.
 *
 * Extracted so that is testable at all — inside the subprocess call it was
 * unreachable, and a sabotage removing it passed.
 */
export function collectUtf8(
  stream: NodeJS.ReadableStream | null | undefined,
  onChunk: (chunk: string) => void,
): void {
  if (!stream) return;
  stream.setEncoding('utf8');
  stream.on('data', (d: string | Buffer) => onChunk(typeof d === 'string' ? d : d.toString('utf8')));
}

/**
 * The S3 error code in an `aws` stderr blob, when there is one.
 *
 * The CLI renders `An error occurred (InvalidObjectState) when calling the
 * GetObject operation: ...`. That code is the AUTHORITATIVE signal for "this
 * object is in an archive tier and must be restored first" — the storage class
 * is not, because a RESTORED object keeps its class and an archived
 * Intelligent-Tiering object reports `INTELLIGENT_TIERING`.
 */
export function awsErrorCode(stderr: string): string | null {
  return /An error occurred \(([A-Za-z0-9_]+)\)/.exec(stderr)?.[1] ?? null;
}

function runAws(
  args: string[],
  src: RestoreSource,
  timeoutMs: number,
): Promise<{ ok: boolean; stdout: string; detail: string; errorCode: string | null }> {
  return new Promise((resolve) => {
    const argv = src.region ? [...args, '--region', src.region] : args;
    const child = spawn('aws', argv, { env: awsEnv(src), stdio: ['ignore', 'pipe', 'pipe'] });
    let stdout = '';
    let stderr = '';
    let settled = false;
    const finish = (r: { ok: boolean; stdout: string; detail: string; errorCode: string | null }): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(r);
    };
    const timer = setTimeout(() => {
      child.kill('SIGKILL');
      finish({ ok: false, stdout: '', detail: `aws timed out after ${timeoutMs}ms`, errorCode: null });
    }, timeoutMs);
    collectUtf8(child.stdout, (chunk) => {
      stdout += chunk;
    });
    collectUtf8(child.stderr, (chunk) => {
      stderr += chunk;
    });
    child.on('error', (err) =>
      finish({ ok: false, stdout: '', detail: err.message, errorCode: null }),
    );
    child.on('close', (code) =>
      finish(
        code === 0
          ? { ok: true, stdout, detail: '', errorCode: null }
          : {
              ok: false,
              stdout,
              detail: stderr.trim().slice(0, 500) || `aws exited with code ${code ?? 'null'}`,
              errorCode: awsErrorCode(stderr),
            },
      ),
    );
  });
}

/**
 * Parse an `s3api list-objects-v2` response.
 *
 * Split out from the subprocess so the TRUNCATION branch below is reachable
 * from a test — a decision buried behind a shell-out cannot be distinguished
 * from a correct one, which is how an earlier sabotage here survived.
 */
export function parseObjectListing(stdout: string): {
  ok: boolean;
  objects: ListedObject[];
  detail: string;
} {
  try {
    const parsed = JSON.parse(stdout || '{}') as {
      Contents?: Array<{ Key?: string; StorageClass?: string }>;
      IsTruncated?: boolean;
      NextContinuationToken?: string;
    };
    // A single S3 page caps at 1000 keys, and a host prefix accumulates every
    // agent's transcripts plus host-logs over the retention window — so this
    // WILL exceed one page on a busy host. The AWS CLI paginates internally and
    // merges the pages (disabled only by `--no-paginate` or `--max-items`,
    // neither of which is passed), so `Contents` is complete.
    //
    // That is an implicit dependency on CLI behaviour, and the failure mode if
    // it ever stops holding is SILENT and badly misleading: a short listing
    // simply would not contain the manifest, the restore would report
    // 'no-manifest', and an operator would conclude nothing was archived for an
    // agent whose entire history is sitting in the bucket. So a truncation
    // marker is a hard failure rather than a trusted full answer.
    if (parsed.IsTruncated === true || parsed.NextContinuationToken) {
      return {
        ok: false,
        objects: [],
        detail:
          'the listing came back truncated, so it cannot be treated as the complete set of ' +
          'archived objects (a missing manifest would read as "nothing archived")',
      };
    }
    const objects = (parsed.Contents ?? [])
      .filter((o): o is { Key: string; StorageClass?: string } => typeof o.Key === 'string')
      .map((o) => ({ key: o.Key, storageClass: o.StorageClass ?? null }));
    return { ok: true, objects, detail: '' };
  } catch (err) {
    return { ok: false, objects: [], detail: `unparseable listing: ${(err as Error).message}` };
  }
}

/**
 * List everything under the source prefix.
 *
 * ENG-8814 is why the minted credential carries `s3:ListBucket` and not only
 * `s3:GetObject`: WITHOUT ListBucket, S3 answers a missing key with 403, not
 * 404. "You may not read this" and "this does not exist" then look identical,
 * and an operator restoring an agent that was never archived gets a permissions
 * error pointing at the wrong problem entirely.
 *
 * The listing also carries each object's storage class, which is where the
 * glacier verdict comes from — one call rather than a HEAD per file.
 */
export async function listSourceObjects(src: RestoreSource): Promise<{
  ok: boolean;
  objects: ListedObject[];
  detail: string;
}> {
  const res = await runAws(
    [
      's3api',
      'list-objects-v2',
      '--bucket',
      src.bucket,
      // Trailing slash so `<prefix>` cannot also enumerate a sibling
      // `<prefix>-other/…`. The session policy anchors on `${prefix}/*` for the
      // same reason.
      '--prefix',
      // Trailing separators stripped first. The API normalises what it returns,
      // but `--key-prefix` is operator-supplied and a `RestoreSource` can be
      // built directly, and `prefix//` matches no key at all — the listing then
      // succeeds with zero objects and the run reports `no-manifest`, the exact
      // misleading verdict the note above is about.
      `${src.keyPrefix.replace(/\/+$/, '')}/`,
      '--output',
      'json',
    ],
    src,
    AWS_META_TIMEOUT_MS,
  );
  if (!res.ok) return { ok: false, objects: [], detail: res.detail };
  return parseObjectListing(res.stdout);
}

/** Fetch one object's bytes to stdout. */
async function getObjectBody(
  src: RestoreSource,
  key: string,
): Promise<{ ok: boolean; body: string; detail: string }> {
  const res = await runAws(
    ['s3', 'cp', `s3://${src.bucket}/${key}`, '-'],
    src,
    AWS_META_TIMEOUT_MS,
  );
  return { ok: res.ok, body: res.stdout, detail: res.detail };
}

/** A candidate manifest that could not be read, and why. */
export interface UnreadableManifest {
  key: string;
  /** From the listing, so it is known even though the fetch failed. */
  storageClass: string | null;
  detail: string;
}

/**
 * The outcome of looking for an agent's drain manifest.
 *
 * `absent` and `unavailable` are deliberately DIFFERENT answers. A null for both
 * collapses "this agent was never archived here" into "we could not read the
 * thing that would have told us" — and the restore core turns the first into
 * `no-manifest`, whose message tells the operator nothing describes this agent.
 * For a manifest sitting in Glacier that message is precisely wrong: the whole
 * history is in the bucket, one restore request away, and the operator has just
 * been told to stop looking.
 */
export type ManifestLookup =
  /** A manifest naming `codeName` was read. The newest, when several were. */
  | { kind: 'found'; manifest: DrainManifest }
  /** Every candidate was read, and none of them was this agent's. */
  | { kind: 'absent' }
  /** No manifest for this agent was read, and at least one candidate failed. */
  | { kind: 'unavailable'; unreadable: UnreadableManifest[] };

/**
 * Find the drain manifest for `codeName` among the listed objects.
 *
 * A host prefix holds one manifest per agent that ever drained from it, so the
 * candidates are filtered by reading each one's `codeName` rather than by
 * pattern-matching the encoded directory segment — the encoding is the SOURCE's
 * and this host cannot compute it (that is the whole reason ENG-9566 records
 * the layout). When a codename has drained more than once, the newest wins.
 *
 * A READ manifest always wins over an unread candidate: the unread one may well
 * belong to another agent, and refusing a restore we have everything for would
 * be its own false negative.
 */
export async function findDrainManifest(
  src: RestoreSource,
  codeName: string,
  objects: ListedObject[],
  /** Injected so the FILTER and the tie-break are testable without an `aws`. */
  getBody: (key: string) => Promise<{ ok: boolean; body: string; detail?: string }> = (key) =>
    getObjectBody(src, key),
): Promise<ManifestLookup> {
  const storageClassByKey = new Map(objects.map((o) => [o.key, o.storageClass]));
  const candidates = objects
    .map((o) => o.key)
    // An exact basename match, not `endsWith('.json')`: the prefix also holds
    // whatever else the host archived, and fetching each one to find out is a
    // read per object against a customer's bucket.
    .filter((k) => basename(k) === DRAIN_MANIFEST_FILENAME);
  const found: DrainManifest[] = [];
  const unreadable: UnreadableManifest[] = [];
  for (const key of candidates) {
    const res = await getBody(key);
    if (!res.ok) {
      // Kept, not dropped. Whether this mattered depends on what the other
      // candidates turn out to hold, which is not known yet.
      unreadable.push({
        key,
        storageClass: storageClassByKey.get(key) ?? null,
        detail: res.detail ?? '',
      });
      continue;
    }
    try {
      const parsed = JSON.parse(res.body) as DrainManifest;
      if (parsed?.codeName === codeName && Array.isArray(parsed.keys)) found.push(parsed);
    } catch {
      // A torn or truncated manifest is skipped rather than fatal — another
      // candidate may still be the one we want.
    }
  }
  if (found.length > 0) {
    // NaN-safe. `Date.parse` yields NaN for an absent or malformed `writtenAt`,
    // and a NaN comparator result reads as "equal" — so ordering silently falls
    // back to input order and an OLDER manifest can win, restoring a stale key
    // set and, worse, a stale `sessionPin`. The candidate guard accepts on
    // `codeName` and `keys` alone, and these bytes come from the source bucket,
    // so this reader must not assume the producer's field is well-formed.
    // Undated manifests sort last rather than being dropped: one is still
    // better than none when it is all there is.
    const at = (m: DrainManifest): number => {
      const t = Date.parse(m.writtenAt);
      return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
    };
    found.sort((a, b) => at(b) - at(a));
    return { kind: 'found', manifest: found[0]! };
  }
  if (unreadable.length > 0) return { kind: 'unavailable', unreadable };
  return { kind: 'absent' };
}

/**
 * The operator-facing reason a manifest lookup came back `unavailable`.
 *
 * Named per candidate, and the offline tier is called out by name, because
 * "restore it to a readable tier first" and "this read is failing" are different
 * next actions.
 */
export function describeUnreadableManifests(unreadable: UnreadableManifest[]): string {
  return unreadable
    .map((u) =>
      isOfflineStorageClass(u.storageClass)
        ? `'${u.key}' is in ${u.storageClass} and must be restored to a readable tier before it can be read`
        : `'${u.key}' could not be read${u.detail ? `: ${u.detail}` : ''}`,
    )
    .join('; ');
}

/**
 * Is this a complete JSONL transcript?
 *
 * Every non-blank line must parse. A truncated download ends mid-line, so the
 * last line is the one that fails — and landing that file is exactly what
 * ENG-5397 spent hours of agent silence on.
 */
export function verifyJsonlTranscript(path: string): boolean {
  let raw: string;
  try {
    raw = readFileSync(path, 'utf-8');
  } catch {
    return false;
  }
  if (raw.trim().length === 0) return false;
  for (const line of raw.split('\n')) {
    if (line.trim().length === 0) continue;
    try {
      JSON.parse(line);
    } catch {
      return false;
    }
  }
  return true;
}

/**
 * Production deps. `objects` comes from a single `listSourceObjects` call so the
 * storage class is known per key without a HEAD per file.
 */
export function restoreTranscriptDeps(args: {
  src: RestoreSource;
  objects: ListedObject[];
  manifest: DrainManifest | null;
  /** The agent's IANA timezone, when known — see `restorePin` below. */
  timezone?: string;
  log: (msg: string) => void;
}): RestoreDeps {
  const storageClassByKey = new Map(args.objects.map((o) => [o.key, o.storageClass]));
  return {
    resolveDestTranscriptDir: (codeName) => sessionTranscriptDir(getProjectDir(codeName)),
    readManifest: () => args.manifest,
    download: async ({ key, tmpPath }) => {
      const storageClass = storageClassByKey.get(key) ?? undefined;
      mkdirSync(dirname(tmpPath), { recursive: true });
      const res = await runAws(
        ['s3', 'cp', `s3://${args.src.bucket}/${key}`, tmpPath, '--only-show-errors'],
        args.src,
        AWS_TIMEOUT_MS,
      );
      return { ok: res.ok, detail: res.detail, storageClass, errorCode: res.errorCode };
    },
    verify: verifyJsonlTranscript,
    commit: (tmpPath, finalPath) => {
      mkdirSync(dirname(finalPath), { recursive: true });
      // Same filesystem, so this is atomic: a reader never sees a partial file
      // under the name the spawner would hand to --resume.
      renameSync(tmpPath, finalPath);
    },
    discard: (tmpPath) => {
      try {
        if (existsSync(tmpPath)) unlinkSync(tmpPath);
      } catch {
        // A leftover .restore-tmp is inert — it is not a *.jsonl, so neither
        // the spawner nor a later drain flush will ever pick it up.
      }
    },
    localMtimeMs: (path) => {
      try {
        return statSync(path).mtimeMs;
      } catch {
        return null;
      }
    },
    sessionFileExists: (codeName, sessionId) =>
      sessionFileExists(getProjectDir(codeName), sessionId),
    restorePin: (codeName, sessionId) => {
      try {
        // `markDailySessionSpawn` dates the pin to the DESTINATION's today and
        // demotes any existing entry to history — which is precisely the write
        // this needs. Copying the source's `current` verbatim would restore
        // yesterday's date, and `getOrCreateDailySession` would roll it over on
        // the very next spawn, silently discarding the restored identity.
        markDailySessionSpawn(codeName, sessionId, new Date(), args.timezone);
        return true;
      } catch {
        return false;
      }
    },
    resumeDisabled: () => isSessionResumeDisabled(),
    log: args.log,
  };
}
